import { MemberIcon } from '../../../../member-web/src/MemberIcon';
import { memberNavigation, memberServices } from '../../../../member-web/src/member-navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  MemberCardTemplate,
  MemberCenterAppearanceV1,
  MemberLevelDto,
  MemberPortalDto,
} from '@pokerroom/types';
import { MemberTierCard } from '../../../../member-web/src/MemberTierCard';
import lightLogo from '../../../../member-web/public/zipa-mark.png';
import darkLogo from '../../../../member-web/public/zipa-mark-white.png';
import { apiClient } from '../../lib/api';

type CardDraft = Pick<MemberCardTemplate, 'showLevelName' | 'showPhone' | 'showTimedPoints'>;
export function MemberPortalPreview({
  appearance,
  card,
  template,
  levels,
}: {
  appearance: MemberCenterAppearanceV1;
  card: CardDraft;
  template?: MemberCardTemplate;
  levels: MemberLevelDto[];
}) {
  const { t } = useTranslation('member');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [style, setStyle] = useState<'standard' | 'watermark'>('standard');
  const [levelId, setLevelId] = useState('sample-standard');
  const [image, setImage] = useState<{ key: string; url: string }>();
  const [imageError, setImageError] = useState('');
  const level = levels.find((item) => item.id === levelId);
  const templateId = template?.id;
  const imageKey = template?.images[levelId]
    ? `${template.id}/${levelId}/${template.images[levelId]}`
    : '';
  useEffect(() => {
    setImage(undefined);
    setImageError('');
    if (!imageKey || !templateId) return;
    const abort = new AbortController();
    let url = '';
    void apiClient
      .get<Blob>(`/members/card-templates/${templateId}/images/${levelId}`, {
        responseType: 'blob',
        signal: abort.signal,
      })
      .then((response) => {
        if (abort.signal.aborted) return;
        url = URL.createObjectURL(response.data);
        setImage({ key: imageKey, url });
      })
      .catch(() => {
        if (!abort.signal.aborted) setImageError('preview.imageError');
      });
    return () => {
      abort.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [imageKey, templateId, levelId]);
  const code = level?.code.trim().toUpperCase();
  const tier = level
    ? level.isZCustomer
      ? 'z'
      : code === 'STANDARD' || code === 'CLASSIC'
        ? 'standard'
        : code === 'VIP'
          ? 'vip'
          : code === 'BLACK'
            ? 'black'
            : null
    : (levelId.slice(7) as 'standard' | 'vip' | 'black' | 'z');
  const member: MemberPortalDto = {
    displayName: t('preview.sampleName'),
    maskedPhone: '09******1234',
    memberNo: 'MB-DEMO-0001',
    level: { name: level?.displayName ?? tier ?? '', visualTier: tier },
    status: 'ACTIVE',
    rewardPoints: '1280',
    prizeCredits: '300',
  };
  return (
    <section className="member-editor-preview-wrap" aria-label={t('preview.title')}>
      <h2>{t('preview.title')}</h2>
      <p>{t('preview.synthetic')}</p>
      <div className="member-editor-preview-options">
        <label>
          {t('preview.theme')}
          <select
            data-testid="preview-theme"
            value={theme}
            onChange={(e) => setTheme(e.target.value as typeof theme)}
          >
            <option value="light">{t('preview.light')}</option>
            <option value="dark">{t('preview.dark')}</option>
          </select>
        </label>
        <label>
          {t('preview.style')}
          <select
            data-testid="preview-style"
            value={style}
            onChange={(e) => setStyle(e.target.value as typeof style)}
          >
            <option value="standard">{t('preview.standard')}</option>
            <option value="watermark">{t('preview.watermark')}</option>
          </select>
        </label>
        <label>
          {t('preview.level')}
          <select
            data-testid="preview-level"
            value={levelId}
            onChange={(e) => setLevelId(e.target.value)}
          >
            {['standard', 'vip', 'black', 'z'].map((value) => (
              <option key={value} value={`sample-${value}`}>
                {t(`preview.${value}`)}
              </option>
            ))}
            {levels.map((item) => (
              <option key={item.id} value={item.id}>
                {item.displayName}
              </option>
            ))}
          </select>
        </label>
      </div>
      {imageError && <p role="status">{t(imageError)}</p>}
      <div
        className="member-editor-preview"
        data-testid="member-portal-preview"
        data-member-theme={theme}
      >
        <h3>{appearance.heading === '會員中心' ? t('preview.home') : appearance.heading}</h3>
        <MemberTierCard
          member={member}
          appearance={{ version: 2, ...card, imageUrl: image?.key === imageKey ? image.url : null }}
          preferences={{ version: 1, theme, cardStyle: style, language: 'zh-TW' }}
          labels={{ showQr: t('preview.qr'), cardImage: t('preview.cardImage') }}
          logos={{ light: lightLogo, dark: darkLogo }}
          open={() => {}}
        />
        <div className="member-preview-qr">
          <MemberIcon kind="qr" />
          {t('preview.qr')}
        </div>
        <div className="member-preview-summary">
          <div>
            <span>{t('preview.playerPoints')}</span>
            <strong>1,280</strong>
          </div>
          {appearance.visibleMetrics.includes('prizeCredits') && (
            <div>
              <span>{t('preview.credits')}</span>
              <strong>300</strong>
            </div>
          )}
          {card.showTimedPoints && (
            <div>
              <span>{t('preview.timedPoints')}</span>
              <strong>0</strong>
              <small>2026-11</small>
              <small>{t('preview.previousTimedPoints')}</small>
            </div>
          )}
        </div>
        <div className="member-preview-booking">
          <MemberIcon kind="booking" />
          <div>
            <small>{t('preview.nextBooking')}</small>
            <strong>{t('preview.onlineBooking')}</strong>
            <span>{t('preview.comingSoon')}</span>
          </div>
        </div>
        <div className="member-preview-shortcuts">
          {['booking', 'redeem', 'tickets', 'referral']
            .map((route) => memberServices.find((item) => item.route === route)!)
            .map((item) => (
              <div key={item.route}>
                <MemberIcon
                  kind={
                    item.route === 'booking'
                      ? 'reserve'
                      : item.route === 'tickets'
                        ? 'tickets'
                        : item.route === 'referral'
                          ? 'referral'
                          : 'redeem'
                  }
                />
                <span>{t(`preview.services.${item.route}`)}</span>
              </div>
            ))}
        </div>
        <div className="member-preview-services">
          {['reservations', 'redemptions', 'class-tickets', 'draws', 'service-history']
            .map((route) => memberServices.find((item) => item.route === route)!)
            .map((item) => (
              <div key={item.route}>
                <span>{t(`preview.services.${item.route}`)}</span>
                <MemberIcon kind="arrow" />
              </div>
            ))}
          <div>
            <span>{t('preview.history')}</span>
            <MemberIcon kind="arrow" />
          </div>
        </div>
        <nav aria-label={t('preview.navigation')}>
          {memberNavigation.map((item) => (
            <span key={item.route} aria-current={item.route === 'card' ? 'page' : undefined}>
              <MemberIcon kind={item.route} />
              {t(`preview.nav.${item.route}`)}
            </span>
          ))}
        </nav>
      </div>
      <p className="member-editor-preview-limit">{t('preview.limitations')}</p>
    </section>
  );
}
